import React, { Component } from 'react';
import authService from '../api-authorization/AuthorizeService';
import './PBI_Analisis.css';

const failedRefreshStatuses = new Set(['failed', 'disabled', 'cancelled']);

export class PBI_Analisis extends Component {
    static displayName = PBI_Analisis.name;

    constructor(props) {
        super(props);

        this.state = {
            config: null,
            error: null,
            loading: true,
            checkingRefreshStatus: true,
            refreshing: false,
            refreshMessage: null,
            refreshElapsedSeconds: 0,
            iframeVersion: 0
        };

        this.datasetRefreshTimer = null;
        this.refreshElapsedTimer = null;
    }

    componentDidMount() {
        this.loadClientConfig();
    }

    componentWillUnmount() {
        this.clearDatasetRefreshTimer();
        this.clearRefreshElapsedTimer();
    }

    getAuthorizationHeaders = async () => {
        const applicationToken = await authService.getAccessToken();
        return applicationToken
            ? { Authorization: `Bearer ${applicationToken}` }
            : {};
    };

    readProblemDetails = async response => {
        try {
            const problem = await response.json();
            return problem.detail || problem.title || `La solicitud falló con el código ${response.status}.`;
        } catch {
            return `La solicitud falló con el código ${response.status}.`;
        }
    };

    loadClientConfig = async () => {
        try {
            const headers = await this.getAuthorizationHeaders();
            const response = await fetch('/api/powerbi/client-config', { headers });

            if (!response.ok) {
                throw new Error(await this.readProblemDetails(response));
            }

            const config = await response.json();
            this.setState(
                { config, error: null, loading: false },
                this.checkForActiveDatasetRefresh);
        } catch (error) {
            console.error('No se pudo cargar la configuración pública de Power BI.', error);
            this.setState({
                error: error instanceof TypeError
                    ? 'No se pudo conectar con el servicio de Power BI.'
                    : error.message || 'No se pudo cargar la configuración de Power BI.',
                loading: false,
                checkingRefreshStatus: false
            });
        }
    };

    checkForActiveDatasetRefresh = async () => {
        try {
            const headers = await this.getAuthorizationHeaders();
            const response = await fetch('/api/powerbi/dataset-refresh', { headers });

            if (response.status === 204) {
                this.setState({ checkingRefreshStatus: false });
                return;
            }

            if (!response.ok) {
                throw new Error(await this.readProblemDetails(response));
            }

            const result = await response.json();

            if (!result.requestId) {
                this.setState({ checkingRefreshStatus: false });
                return;
            }

            this.setState({
                checkingRefreshStatus: false,
                refreshing: true,
                refreshMessage: 'Power BI ya está actualizando el conjunto de datos...',
                refreshElapsedSeconds: 0
            });
            this.startRefreshElapsedTimer();
            this.scheduleDatasetRefreshStatus(result.requestId);
        } catch (error) {
            console.error('No se pudo verificar la actualización activa de Power BI.', error);
            this.setState({
                checkingRefreshStatus: false,
                error: error instanceof TypeError
                    ? 'No se pudo conectar con el servicio de Power BI.'
                    : error.message || 'No se pudo verificar el estado de actualización.'
            });
        }
    };

    startDatasetRefresh = async () => {
        if (this.state.refreshing) {
            return;
        }

        this.clearDatasetRefreshTimer();
        this.setState({
            refreshing: true,
            error: null,
            refreshMessage: 'Iniciando la actualización del conjunto de datos...',
            refreshElapsedSeconds: 0
        });
        this.startRefreshElapsedTimer();

        try {
            const headers = await this.getAuthorizationHeaders();
            const response = await fetch('/api/powerbi/dataset-refresh', {
                method: 'POST',
                headers
            });

            if (!response.ok) {
                throw new Error(await this.readProblemDetails(response));
            }

            const result = await response.json();

            if (!result.requestId) {
                throw new Error(
                    'Power BI aceptó la actualización, pero no devolvió un identificador de solicitud.');
            }

            this.setState({
                refreshMessage: 'Power BI está actualizando el conjunto de datos...'
            });
            this.scheduleDatasetRefreshStatus(result.requestId);
        } catch (error) {
            console.error('No se pudo iniciar la actualización del dataset de Power BI.', error);
            this.clearRefreshElapsedTimer();
            this.setState({
                error: error instanceof TypeError
                    ? 'No se pudo conectar con el servicio de Power BI.'
                    : error.message || 'No se pudo iniciar la actualización del conjunto de datos.',
                refreshing: false,
                refreshMessage: null
            });
        }
    };

    scheduleDatasetRefreshStatus = requestId => {
        this.clearDatasetRefreshTimer();
        this.datasetRefreshTimer = window.setTimeout(
            () => this.checkDatasetRefreshStatus(requestId),
            this.state.config.refreshStatusPollMilliseconds);
    };

    checkDatasetRefreshStatus = async requestId => {
        try {
            const headers = await this.getAuthorizationHeaders();
            const response = await fetch(`/api/powerbi/dataset-refresh/${requestId}`, { headers });

            if (!response.ok) {
                throw new Error(await this.readProblemDetails(response));
            }

            const result = await response.json();
            const status = (result.status || 'Unknown').toLowerCase();

            if (status === 'completed') {
                this.clearDatasetRefreshTimer();
                this.clearRefreshElapsedTimer();
                this.setState(previousState => ({
                    refreshing: false,
                    refreshMessage: `El conjunto de datos se actualizó correctamente en ${this.formatElapsedTime(previousState.refreshElapsedSeconds)}.`,
                    iframeVersion: previousState.iframeVersion + 1
                }));
                return;
            }

            if (failedRefreshStatuses.has(status)) {
                this.clearDatasetRefreshTimer();
                this.clearRefreshElapsedTimer();
                this.setState({
                    error: 'La actualización del conjunto de datos falló en Power BI.',
                    refreshing: false,
                    refreshMessage: null
                });
                return;
            }

            this.scheduleDatasetRefreshStatus(requestId);
        } catch (error) {
            console.error('No se pudo consultar el estado de actualización de Power BI.', error);
            this.clearDatasetRefreshTimer();
            this.clearRefreshElapsedTimer();
            this.setState({
                error: error instanceof TypeError
                    ? 'No se pudo conectar con el servicio de Power BI.'
                    : error.message || 'No se pudo consultar el estado de la actualización.',
                refreshing: false,
                refreshMessage: null
            });
        }
    };

    clearDatasetRefreshTimer = () => {
        if (this.datasetRefreshTimer !== null) {
            window.clearTimeout(this.datasetRefreshTimer);
            this.datasetRefreshTimer = null;
        }
    };

    startRefreshElapsedTimer = () => {
        this.clearRefreshElapsedTimer();
        this.refreshElapsedTimer = window.setInterval(() => {
            this.setState(previousState => ({
                refreshElapsedSeconds: previousState.refreshElapsedSeconds + 1
            }));
        }, 1000);
    };

    clearRefreshElapsedTimer = () => {
        if (this.refreshElapsedTimer !== null) {
            window.clearInterval(this.refreshElapsedTimer);
            this.refreshElapsedTimer = null;
        }
    };

    formatElapsedTime = totalSeconds => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    render() {
        const {
            config,
            error,
            loading,
            checkingRefreshStatus,
            refreshing,
            refreshMessage,
            refreshElapsedSeconds,
            iframeVersion
        } = this.state;

        if (loading) {
            return <div className="power-bi-message">Cargando informe de Power BI...</div>;
        }

        if (!config) {
            return (
                <div className="power-bi-message power-bi-error" role="alert">
                    <p>{error || 'No se pudo cargar la configuración de Power BI.'}</p>
                    <button type="button" onClick={this.loadClientConfig}>
                        Reintentar
                    </button>
                </div>
            );
        }

        const viewportStyle = { height: `${config.iframeHeight}px` };
        const iframeStyle = {
            height: `calc(100% + ${config.iframeFooterCropPixels}px)`
        };

        return (
            <div className="power-bi-wrapper">
                <div className="power-bi-toolbar">
                    {refreshMessage && (
                        <span className="power-bi-refresh-status" role="status" aria-live="polite">
                            {refreshMessage}
                            {refreshing && (
                                <span className="power-bi-refresh-elapsed">
                                    {' '}({this.formatElapsedTime(refreshElapsedSeconds)})
                                </span>
                            )}
                        </span>
                    )}
                    <button
                        type="button"
                        className="power-bi-refresh-button"
                        onClick={this.startDatasetRefresh}
                        disabled={checkingRefreshStatus || refreshing}
                    >
                        {(checkingRefreshStatus || refreshing) && (
                            <span
                                className="power-bi-refresh-spinner"
                                aria-hidden="true"
                            />
                        )}
                        <span>
                            {checkingRefreshStatus
                                ? 'Verificando...'
                                : refreshing
                                    ? 'Actualizando...'
                                    : 'Actualizar datos'}
                        </span>
                    </button>
                </div>
                {error && (
                    <div className="power-bi-message power-bi-error" role="alert">
                        {error}
                    </div>
                )}
                <div className="power-bi-iframe-viewport" style={viewportStyle}>
                    <iframe
                        key={iframeVersion}
                        title="PBI Analisis"
                        className="power-bi-legacy-iframe"
                        style={iframeStyle}
                        src={config.reportUrl}
                        frameBorder="0"
                        allowFullScreen
                    />
                </div>
            </div>
        );
    }
}
